import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { NavigationEnd, Router, Scroll } from '@angular/router';
import { PanelLayout, SbLayout } from '@models';
import { SbEvent, SbEventBusService, SbEventTopic, SbPrintService } from '../../services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-sb-print-layout',
    templateUrl: './sb-print-layout.component.html',
    styleUrls: ['./sb-print-layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbPrintLayoutComponent implements OnInit, OnDestroy {
    unsubscribe$ = new Subject();
    @Input() panelId: string;
    @Input() grid: any;

    public formattedPrintFormData$ = this.printService.printFormData$;
    constructor(
        private eventBus: SbEventBusService,
        private printService: SbPrintService,
        private cdr: ChangeDetectorRef,
        router: Router
    ) {
        // TODO: this should be refactored
        router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
            if (!(event instanceof NavigationEnd) && !(event instanceof Scroll)) {
                this.printService.changePrintStatus(false);
                this.printService.checkPrintingEnables(false);
            }
        });
    }

    btnPrint(event: any) {
        const payload: SbEvent = {
            source: this.panelId,
            topic: SbEventTopic.Print,
            payload: 'Printing'
        };
        this.eventBus.emit(payload);
        setTimeout(() => {
            print();
        }, 1500);
    }

    @HostListener('window:afterprint', ['$event'])
    onAfterPrint(event) {
        this.donePrinting();
    }

    donePrinting() {
        const payload: SbEvent = {
            source: this.panelId,
            topic: SbEventTopic.Print,
            payload: 'PrintingDone'
        };
        this.eventBus.emit(payload);
    }

    ngOnDestroy() {
        this.cancelPrintPreview();
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    cancelPrintPreview() {
        this.printService.changePrintStatus(false);
        const payload: SbEvent = {
            source: this.panelId,
            topic: SbEventTopic.Print,
            payload: 'notPrint'
        };
        this.eventBus.emit(payload);
    }

    // Todo - extract to utility method - being used here and in panel-component.ts DRY
    getLayoutClass(sbLayout: SbLayout): string {
        const width = `col-${sbLayout.width || 12}`;
        if (sbLayout.width) {
            return width;
        } else {
            const widthSm = `col-sm-${sbLayout.widthSm || 12}`;
            const widthMd = `col-md-${sbLayout.widthMd || 6}`;
            const widthLg = `col-lg-${sbLayout.widthLg || 4}`;
            const widthXl = `col-xl-${sbLayout.widthXl || 4}`;
            const fixedAt = sbLayout.fixAtSm
                ? 'sb-p-col-fixed-small'
                : sbLayout.fixedAtMd
                ? 'sb-p-col-fixed-medium'
                : sbLayout.fixedAtLg
                ? 'sb-p-col-fixed-large'
                : sbLayout.fixedAtXl
                ? 'sb-p-col-fixed-huge'
                : sbLayout.fixedAtLandscapePrint
                ? 'sb-p-col-landscape-print'
                : sbLayout.fixedAtPortraitPrint
                ? 'sb-p-col-portrait-print'
                : '';
            return [
                `col-${sbLayout.widthSm || 12}`,
                widthSm,
                widthMd,
                widthLg,
                widthXl,
                fixedAt
            ].join(' ');
        }
    }

    hasPanelLayouts(panelLayout: PanelLayout): boolean {
        return panelLayout.panelLayouts != null && panelLayout.panelLayouts.length > 0;
    }

    hasTabbedPanels(panelLayout: PanelLayout): boolean {
        return panelLayout.tabbedPanels != null && panelLayout.tabbedPanels.length > 0;
    }

    handleTabChange(e) {
        const event: SbEvent = {
            source: 'DoIneedThisInThisCase',
            topic: SbEventTopic.TabPanelTabChange,
            payload: {
                tabIndex: e.index
            }
        };
        this.eventBus.emit(event);
    }

    // Show/Hide main div type
    // ------------------------------
    //
    // Fetch emitted events from child component that gets display output for
    // the app-sb-panel component.
    //
    // * This is not an optimal solution but there is no better way to do this
    // without refactoring multiple components
    displayPrintSections: { [key: string]: { panelId: string; display: boolean } } = {};

    activeMainDiv(id: string): boolean {
        // Positions report should always show when printing
        if (id === 'positions-report') return true;

        const displayStatus = Object.values(this.displayPrintSections).filter(
            (o) => o.panelId === id
        );
        if (displayStatus.length > 0) {
            // Event has been pushed from child component and that value was
            // { ... display: false }
            if (displayStatus.every((d) => d.display === false)) {
                // This styling makes sure that this section does not get printed
                return false;
            }
        }

        return true;
    }

    ngOnInit() {
        this.eventBus
            .getObservable(SbEventTopic.DisplayOutputSet)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((event) => {
                if (event && event.payload) {
                    // Set the display status for the given panelId
                    this.displayPrintSections[event.payload.widgetId] = {
                        panelId: event.payload.panelId,
                        display: event.payload.display
                    };

                    this.cdr.detectChanges();
                }
            });
    }

    // End of Show/Hide main div
    // ------------------------------
}
